import { Col, Row } from 'antd';
import { DateTime } from 'luxon';
import React from 'react';

import FormattedBilletStatement from '@portal/components/FormattedBilletStatement/FormattedBilletStatement';
import { cashback, payment } from '@portal/config/app.json';
import { VITE_APPLICATION_NAME } from '@portal/config/env';
import { TransactionStatus } from '@portal/enum/approvalsType';
import { useNavigation } from '@portal/hooks/useNavigation';
import { TransactionFlow, TransactionType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import {
  formatPixKey,
  isCpf,
  maskHiddenCpfOrCnpj,
} from '@portal/services/masks';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';

interface IProps {
  transaction: bank.Transaction | null;
  username: string;
  account?: models.Account;
}

type Field = {
  label: string;
  value: string;
};

type Section = {
  title: string;
  fields: Field[];
};

const TransactionDetails = ({ transaction, username }: IProps) => {
  const navigate = useNavigation<{
    isExtract?: boolean;
    isEscrow?: boolean;
    isCashback?: boolean;
    isPayable?: boolean;
    isReceivable?: boolean;
  }>();

  const getDestinyName = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.BILLET:
          return (
            transaction.shared?.destiny.userName ||
            transaction.billetInfo?.beneficiary?.name
          );

        case TransactionType.P2P:
          return transaction.p2pInfo?.payeeName;

        case TransactionType.TED:
          return (
            transaction.shared?.destiny.userName || transaction.tedInfo?.name
          );

        case TransactionType.CASHBACK:
          return transaction.cashbackInfo?.payeeName;

        case TransactionType.PIX:
          return transaction.shared?.destiny?.userName || '';

        default:
          if (transaction.shared?.destiny?.userName) {
            return transaction.shared.destiny.userName;
          }
          if (transaction.flow === TransactionFlow.CASH_IN) {
            return username ?? '';
          }
          return '';
      }
    }
  };

  const getOriginName = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return transaction.p2pInfo?.payerName;

        case TransactionType.CASHBACK:
          return transaction.cashbackInfo?.payerName;

        case TransactionType.PIX:
          if (
            transaction.pixInfo &&
            transaction.flow === TransactionFlow.CASH_IN
          ) {
            return transaction.pixInfo.name;
          } else {
            return username ?? '';
          }

        default:
          if (transaction.flow === TransactionFlow.CASH_OUT) {
            return username ?? '';
          } else {
            if (transaction.shared) return transaction.shared?.origin.userName;
            return '';
          }
      }
    }
  };

  const getBankNameDestiny = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return VITE_APPLICATION_NAME;

        case TransactionType.PIX:
          return transaction?.shared?.destiny.bankName;

        default:
          if (
            transaction.shared &&
            transaction.shared.destiny &&
            transaction.shared.destiny.bankName
          ) {
            return transaction.shared.destiny.bankName;
          } else {
            return '';
          }
      }
    }
  };

  const getBankNameOrigin = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return VITE_APPLICATION_NAME;

        case TransactionType.PIX:
          return transaction?.shared?.origin.bankName;

        default:
          if (
            transaction.shared &&
            transaction.shared.origin &&
            transaction.shared.origin.bankName
          ) {
            return transaction.shared.origin.bankName;
          } else {
            return '';
          }
      }
    }
  };

  const originNameResult = getOriginName();

  const getTransactionTypeLabel = () => {
    if (!transaction?.type) return null;

    if (
      transaction.type === TransactionType.PIX &&
      transaction.flow === TransactionFlow.CASH_IN &&
      (transaction.isReversed || transaction.pixInfo?.isReversed)
    ) {
      return translate(
        'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PIX_REVERSAL'
      );
    }

    if (transaction.transactionScheduleAt) {
      switch (transaction.type) {
        case TransactionType.PIX:
          return translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_PIX_TRANSACTION'
          );
        case TransactionType.TED:
          return translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_TED_TRANSACTION'
          );
        case TransactionType.BILLET:
          return translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_BILLET_TRANSACTION'
          );
        default:
          return null;
      }
    }

    if (!transaction.isReversed) {
      return getEnumDescription(transaction.type, 'TRANSACTION_TYPE');
    }

    return null;
  };

  const getAmountLabel = () => {
    if (!transaction?.amount) return null;

    if (navigate?.location?.state?.isCashback) {
      if (cashback.sideAccount) {
        return `${transaction.amount} ${translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.POINTS'
        )}`;
      }
      return moneyFormat(transaction.amount);
    }

    return moneyFormat(transaction.amount);
  };

  const renderSectionHeader = () => {
    const typeLabel = getTransactionTypeLabel();
    const amountLabel = getAmountLabel();

    return (
      <div className="users-transactions-details__section">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            {typeLabel ? (
              <div className="users-transactions-details__field">
                <h2 className="users-transactions-details__field__title">
                  {translate(
                    'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                  )}
                </h2>
                <p className="users-transactions-details__field__text">
                  {typeLabel}
                </p>
              </div>
            ) : null}
          </Col>
          {amountLabel ? (
            <Col xs={24} sm={12}>
              <div className="users-transactions-details__field">
                <h2 className="users-transactions-details__field__title">
                  {translate(
                    'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.AMOUNT'
                  )}
                </h2>
                <p className="users-transactions-details__field__text">
                  {amountLabel}
                </p>
              </div>
            </Col>
          ) : null}
        </Row>
      </div>
    );
  };

  const renderDateSection = () => {
    if (!transaction) return null;

    const fields: { label: string; value: string }[] = [];

    if (transaction.transactionScheduleAt) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_DATE'
        ),
        value: DateTime.fromISO(
          transaction.transactionScheduleAt
        ).toLocaleString(DateTime.DATE_SHORT),
      });
    }

    if (transaction.transactionAt) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.CREATED_AT'
        ),
        value: DateTime.fromISO(transaction.transactionAt).toLocaleString(
          DateTime.DATETIME_SHORT_WITH_SECONDS
        ),
      });
    }

    if (
      transaction.transactionReversalAt &&
      transaction.type === TransactionType.P2P &&
      (transaction.status === TransactionStatus.REVERSAL ||
        transaction.status === TransactionStatus.CANCELLED)
    ) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.REVERSAL_DATE'
        ),
        value: DateTime.fromISO(
          transaction.transactionReversalAt
        ).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
      });
    }

    if (transaction.billetInfo?.dueDate) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DUE_DATE'
        ),
        value: DateTime.fromISO(transaction.billetInfo.dueDate).toLocaleString(
          DateTime.DATE_SHORT
        ),
      });
    }

    if (!fields.length) return null;

    return (
      <div className="users-transactions-details__section">
        <Row gutter={[16, 16]}>
          {fields.map((field, index) => (
            <Col xs={24} sm={12} key={index}>
              <div className="users-transactions-details__field">
                <h2 className="users-transactions-details__field__title">
                  {field.label}
                </h2>
                <p className="users-transactions-details__field__text">
                  {field.value}
                </p>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderBilletDetails = () => {
    if (!transaction) return null;

    const fields: Field[] = [];

    if (transaction.billetInfo?.interest) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.INTEREST'
        ),
        value: moneyFormat(transaction.billetInfo.interest),
      });
    }

    if (transaction.billetInfo?.fine) {
      fields.push({
        label: translate('COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.FINE'),
        value: moneyFormat(transaction.billetInfo.fine),
      });
    }

    if (
      transaction.type === TransactionType.BILLET &&
      payment.showDiscountField &&
      transaction.billetInfo?.discount !== undefined &&
      transaction.billetInfo?.discount !== null
    ) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DISCOUNT'
        ),
        value: moneyFormat(transaction.billetInfo.discount),
      });
    }

    if (!fields.length) return null;

    return (
      <div className="users-transactions-details__section">
        <Row gutter={[16, 16]}>
          {fields.map((field, index) => (
            <Col xs={24} sm={12} key={index}>
              <div className="users-transactions-details__field">
                <h2 className="users-transactions-details__field__title">
                  {field.label}
                </h2>
                <p className="users-transactions-details__field__text">
                  {field.value}
                </p>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderSharedSection = () => {
    if (!transaction?.shared) return null;

    const sections: Section[] = [];

    if (transaction.shared.origin) {
      const originFields: Field[] = [];

      if (originNameResult) {
        originFields.push({
          label: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.NAME'
          ),
          value: originNameResult,
        });
      }

      if (transaction.shared.origin.userTaxIdentifier) {
        const label =
          transaction.type === TransactionType.PIX &&
          transaction.flow === TransactionFlow.CASH_IN
            ? translate(
                'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TAX_IDENTIFIER.BOTH'
              )
            : translate(
                `COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TAX_IDENTIFIER.${
                  isCpf(transaction.shared.origin.userTaxIdentifier ?? '')
                    ? 'PERSON'
                    : 'COMPANY'
                }`
              );

        originFields.push({
          label,
          value: maskHiddenCpfOrCnpj(
            transaction.shared.origin.userTaxIdentifier ?? ''
          ),
        });
      }

      if (getBankNameOrigin()) {
        originFields.push({
          label: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.BANK'
          ),
          value: getBankNameOrigin() || '',
        });
      }

      if (originFields.length) {
        sections.push({
          title: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TITLE'
          ),
          fields: originFields,
        });
      }
    }

    if (transaction.shared.destiny) {
      const destinyFields: Field[] = [];

      if (getDestinyName()) {
        destinyFields.push({
          label: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.NAME'
          ),
          value: getDestinyName() || '',
        });
      }

      if (transaction.shared.destiny.userTaxIdentifier) {
        const label =
          (transaction.type === TransactionType.PIX ||
            transaction.type === TransactionType.BILLET) &&
          transaction.flow === TransactionFlow.CASH_OUT
            ? translate(
                'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TAX_IDENTIFIER.BOTH'
              )
            : translate(
                `COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TAX_IDENTIFIER.${
                  isCpf(transaction.shared.destiny.userTaxIdentifier ?? '')
                    ? 'PERSON'
                    : 'COMPANY'
                }`
              );

        destinyFields.push({
          label,
          value: maskHiddenCpfOrCnpj(
            transaction.shared.destiny.userTaxIdentifier ??
              transaction.billetInfo?.beneficiary?.taxIdentifier ??
              ''
          ),
        });
      }

      if (getBankNameDestiny()) {
        destinyFields.push({
          label: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.BANK'
          ),
          value: getBankNameDestiny() || '',
        });
      }

      if (transaction.shared.destiny.userBankBranch) {
        destinyFields.push({
          label: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.BRANCH'
          ),
          value: transaction.shared.destiny.userBankBranch.padStart(4, '0'),
        });
      }

      if (destinyFields.length) {
        sections.push({
          title: translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TITLE'
          ),
          fields: destinyFields,
        });
      }
    }

    if (!sections.length) return null;

    return (
      <>
        <div className="users-transactions-details__section">
          <Row gutter={[16, 16]}>
            {sections.map((section, index) => (
              <Col xs={24} md={12} key={index}>
                <div className="users-transactions-details__field">
                  <h2 className="users-transactions-details__field__title">
                    {section.title}
                  </h2>
                  {section.fields.map((field, idx) => (
                    <p
                      key={idx}
                      className="users-transactions-details__field__text"
                    >
                      <span>{field.label}</span>
                      <strong>{field.value}</strong>
                    </p>
                  ))}
                </div>
              </Col>
            ))}
          </Row>
        </div>
        <div className="users-transactions-details__line" />
      </>
    );
  };

  const renderIdentifiersSection = () => {
    if (!transaction) return null;

    const fields: {
      label: string;
      value: string;
      col?: { xs: number; sm?: number };
      isBlock?: boolean;
    }[] = [];

    if (transaction.pixInfo?.endToEndId) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PIX_IDENTIFIER'
        ),
        value: transaction.pixInfo.endToEndId,
        col: { xs: 24, sm: 12 },
      });
    }

    if (transaction.pixInfo?.key) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.KEY'
        ),
        value: formatPixKey(transaction.pixInfo.key) || '',
        col: { xs: 24, sm: 12 },
      });
    }

    if (transaction.bankId) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TRANSACTION_ID'
        ),
        value: transaction.bankId,
        col: { xs: 24, sm: 12 },
      });
    }

    if (
      transaction.billetInfo &&
      transaction.type === TransactionType.BILLET &&
      transaction.billetInfo.typeableLine
    ) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.BARCODE'
        ),
        value: transaction.billetInfo.typeableLine,
        col: { xs: 24 },
      });
    }

    if (transaction.description) {
      fields.push({
        label: translate(
          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DESCRIPTION'
        ),
        value: transaction.description,
        col: { xs: 24, sm: 12 },
        isBlock: true,
      });
    }

    if (!fields.length) return null;

    return (
      <div className="users-transactions-details__section">
        <Row gutter={[16, 16]}>
          {fields.map((field, index) => (
            <Col key={index} xs={field.col?.xs ?? 24} sm={field.col?.sm}>
              <div className="users-transactions-details__field">
                <h2 className="users-transactions-details__field__title">
                  {field.label}
                </h2>
                <p
                  className={`users-transactions-details__field__text ${
                    field.isBlock
                      ? 'users-transactions-details__field__text--block'
                      : ''
                  }`}
                >
                  {field.value}
                </p>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <div className="users-transactions-details">
      {transaction?.billetInfo?.receiptFormatted ? (
        <Row className="users-transactions-details__billet">
          <Col span={12}>
            <FormattedBilletStatement
              billet={transaction?.billetInfo.receiptFormatted}
            />
          </Col>
        </Row>
      ) : (
        <>
          {renderSectionHeader()}
          {renderDateSection()}
          {renderBilletDetails()}
          <div className="users-transactions-details__line" />
          {renderSharedSection()}
          {renderIdentifiersSection()}
        </>
      )}
    </div>
  );
};

export default TransactionDetails;
